<template>
    <div class="flex border-t border-b -mt-px border-gray-200 draggable bg-white" :key="renderKey">
        <!-- Draggable handle. -->
        <div class="w-6 self-start py-1.5 border-r border-gray-200" v-if="!readonly">
            <div class="w-5 h-5 flex items-center">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move handle"></i>
            </div>
        </div>
        <!-- Material. -->
        <div class="flex flex-col -ml-px flex-1 relative">
            <request-for-material-item-material-search #default="{ close, open, query, setQuery }"
                                                       :resource-planning-report="resourcePlanningReport"
                                                       ref="materialSearch"
                                                       @addMaterials="addMaterials"
                                                       @updateMaterials="updateMaterials"
                                                       :request-for-materials="requestForMaterials"
                                                       :request-for-material-item="item"
                                                       class="text-xs mx-0 m-0 p-0 border-none text-blue-600 flex w-full"
                                                       trigger-class="flex w-full"
                                                       menuClass="w-[28rem]"
            >
                <page-cell
                    ref="material"
                    :read-only="readonly"
                    :use-textarea-readonly="true"
                    class="text-sm -my-px px-2 py-1.5 border border-gray-200 text-gray-500"
                    :placeholder="'Material...'"
                    v-model="item.material"
                    @focus="open"
                    @input="setQuery(item.material);$emit('changed')"
                    @blur="close"
                ></page-cell>
            </request-for-material-item-material-search>
        </div>
        <!-- Unit of Measure. -->
        <div class="w-16 flex -ml-px bg-white">
            <page-cell
                ref="uom"
                :read-only="readonly"
                :use-textarea-readonly="true"
                class="text-sm -my-px px-2 py-1.5 border border-gray-200"
                :placeholder="'Uom...'"
                v-model="item.uom"
                @blur="requestForMaterials.saveAndRefresh()"
                @input="$emit('changed')"
            ></page-cell>
        </div>
        <!-- Quantity. -->
        <div class="flex -ml-px bg-white"
             :class="['Complete','In Progress','Acknowledged'].includes(requestForMaterials.status)?'w-32':'w-16'">
            <div
                v-if="['Complete','In Progress','Acknowledged'].includes(requestForMaterials.status)"
                class="w-full resize-none overflow-hidden overflow-x-auto whitespace-nowrap shadow-none relative -my-px pt-1.5 pl-1.5 text-gray-600 border bg-white border-gray-200"
                v-text="parseFloat(item.resolved)+' / '+item.quantity"
            ></div>
            <page-cell
                v-else
                ref="quantity"
                :read-only="readonly"
                :use-textarea-readonly="true"
                class="text-sm -my-px px-2 py-1.5 border border-gray-200"
                :placeholder="'Qty...'"
                v-model="item.quantity"
                @blur="requestForMaterials.saveAndRefresh()"
                @input="$emit('changed')"
            ></page-cell>
        </div>
        <request-for-material-item-item-select #default="{ close, open, query, setQuery }"
                                               :resource-planning-report="resourcePlanningReport"
                                               ref="itemSelect"
                                               @selected:item="selectItem"
                                               :request-for-materials="requestForMaterials"
                                               :request-for-material-item="item"
                                               class="w-1/3 -ml-px -my-px bg-white border border-gray-200 px-1 py-1"
                                               trigger-class=""
                                               menuClass="w-[28rem]"
        >
            <div class="flex truncate">
                <div v-if="item.itemReference"
                     class="truncate max-w-full inline-block text-xs ml-0.5 px-2 mt-0.5 py-0 rounded bg-gray-50 border border-gray-200 flex-shrink text-gray-500 hover:text-blue-600 cursor-pointer"
                     v-text="item.itemReference.item.reference + ' - ' +item.itemReference.item.name"
                     @click="readonly?false:open()">
                </div>
                <div v-else
                     class="truncate max-w-full inline-block text-xs ml-0.5 px-2 mt-0.5 py-0 rounded bg-red-50 border border-gray-200 flex-shrink text-red-500 hover:text-blue-600 cursor-pointer"
                     v-text="'Not Set'" @click="readonly?false:open()">
                </div>
            </div>
        </request-for-material-item-item-select>
        <!-- Status. -->
        <div class="w-16 -ml-px flex -mt-px -mb-px -ml-px mr-0 flex justify-center border border-gray-200 bg-white"
             v-if="['Complete','In Progress','Acknowledged'].includes(requestForMaterials.status)">
            <div class="flex w-8">
                <div class="flex w-full -mt-px -mb-px ml-0 mr-0 flex justify-center">
                <span
                    class="fas fa-check text-green-400 bg-transparent text-xs mt-1 h-5 p-1 border border-white rounded-md"
                    v-if="item.resolvedStatus==='Resolved'"></span>
                    <span
                        class="far fa-clock text-green-400 bg-transparent text-xs mt-1 h-5 p-1 border border-white rounded-md"
                        v-if="item.resolvedStatus==='Partially Resolved'"></span>
                    <span
                        class="far fa-clock text-blue-500 bg-transparent text-xs mt-1 h-5 p-1 border border-white rounded-md"
                        v-if="item.resolvedStatus==='Pending'"></span>
                </div>
            </div>
            <ui-dropdown menu-class="p-3 w-[22em]"
                         v-if="requestForMaterials.status!=='Awaiting Acknowledgement' && item.deliveryDates && item.deliveryDates.length"
                         container-class="mx-1 flex flex-shrink"
                         trigger-class="flex flex-1"
            >
                <template #default="{ toggle, open, close}">
                    <div @mouseover="open()" @mouseleave="close" class="">
                        <span
                            class="far fa-truck text-gray-400 bg-transparent text-xs mt-1 h-5 py-1 px-0 border border-white rounded-md"></span>
                    </div>
                </template>
                <template #menu="{ close }">
                    <div class="w-full p-0 z-30 text-xs">
                        <div class="w-full font-medium text-gray-400 mb-1">
                            <div
                                class="flex mt-0 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                                <div class="border-l border-gray-200 px-2 py-1 -mx-px flex-grow text-xs ">Date</div>
                                <div class="border-l border-gray-200 border-r px-2 py-1 -ml-px w-20 text-xs">Quantity
                                </div>
                            </div>
                            <template v-for="(line,key) in item.deliveryDates">
                                <div class="flex border-b border-gray-200">
                                    <div class="border-l border-gray-200 px-2 py-1 -mx-px flex-grow text-xs "><span
                                        class="font-semibold text-gray-600" v-text="line.date"></span>
                                    </div>
                                    <div class="border-l border-r border-gray-200 px-2 py-1 -ml-px w-20 text-xs"
                                         v-text="line.quantity">
                                    </div>
                                </div>
                                <div class="flex border-b" v-if="line.notes">
                                    <div class="border-l border-r border-gray-200 px-2 py-1 -mx-px flex-grow text-xs">
                                        Note: {{ line.notes }}
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </template>
            </ui-dropdown>
        </div>
        <!-- Dropdown. -->
        <div class="w-8 flex-shrink-0 self-start py-1.5" v-if="!readonly">
            <ui-dropdown menu-class="p-3 w-96">
                <template #default="{ toggle }">
                    <button @click="toggle"
                            class="ml-2 w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                        <i class="fa fa-ellipsis-h fa-fw"></i>
                    </button>
                </template>
                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-trash" danger @click="$emit('removeMaterial',item); close()">
                        Remove material
                    </ui-dropdown-item>
                    <template v-if="item.itemReference">
                        <ui-dropdown-divider/>
                        <ui-dropdown-item icon="fas fa-trash" @click="$emit('removeSectionMaterial',item); close()">
                            Remove matching section materials
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" @click="$emit('removeTaskMaterial',item); close()">
                            Remove matching task materials
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" @click="$emit('removeItemMaterial',item); close()">
                            Remove matching item materials
                        </ui-dropdown-item>
                    </template>
                </template>
            </ui-dropdown>
        </div>

    </div>
</template>

<script>

import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiSelect from "../../ui/UiSelect.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import PageCell from "../../ui/PageCell.vue";
import RequestForMaterialItemMaterialSearch from "./RequestForMaterialItemMaterialSearch.vue";
import RequestForMaterialItemItemSelect from "./RequestForMaterialItemItemSelect.vue";
import * as math from "mathjs";

export default {
    components: {
        RequestForMaterialItemMaterialSearch,
        RequestForMaterialItemItemSelect,
        PageCell,
        AutoresizableTextarea,
        UiSelect, UiButtonNew, UiDropdown, UiDropdownItem, UiDropdownDivider
    },
    props: {
        requestForMaterials: Object,
        item: Object,
        readonly: false,
        resourcePlanningReport: Object,
        grouped: false,
    },
    data() {
        return {
            renderKey: 0,
        }
    },
    computed: {},
    methods: {
        save() {

        },
        addMaterials(materials) {
            this.updateMaterials(materials);
//            if (materials.length) {
//                this.$emit("addMaterials", materials);
//                this.$emit('changed');
//            }
        },
        updateMaterials(materials) {
            if (materials.length) {
                if (materials.length >= 1) {
                    let {material, section, task, item} = materials.shift();
                    this.item.material = material.name;
                    this.item.quantity = math.format(material.required, {precision: 14});
                    this.item.uom = material.unitOfMeasure;
                    this.item.itemReference = {
                        section: {id: section.id, name: section.name},
                        task: {id: task.id, reference: task.reference, name: task.name},
                        item: {id: item.id, reference: item.reference, name: item.name},
                    }
                    this.renderKey++;
                }
                this.$emit("addMaterials", materials);
                this.$emit('changed');
                setTimeout(() => this.$refs.quantity.$el.focus(), 50)
            }
        },
        selectItem(section, task, item) {
            if (section) {
                this.item.itemReference = {
                    section: {id: section.id, name: section.name},
                    task: {id: task.id, reference: task.reference, name: task.name},
                    item: {id: item.id, reference: item.reference, name: item.name},
                }
            } else {
                this.item.itemReference = null
            }
            this.$emit('changed');
            this.renderKey++;
        },
    },
    async mounted() {

    }
}
</script>
